import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { usePageSeo, imageLd, crumbsLd, seoCopy, SITE } from "@/lib/seo";
import { CATEGORIES } from "@/lib/api";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { GalleryGrid } from "@/components/site/GalleryGrid";
import { PhotoImg } from "@/components/site/PhotoImg";

export const PageHero = ({ kicker, title, lead, photo, testid }) => (
  <section data-testid={testid} className="relative flex min-h-[64vh] items-end overflow-hidden pb-14 pt-32 md:min-h-[72vh] md:pb-20">
    {photo && <PhotoImg photo={photo} eager sizes="100vw" className="!absolute inset-0 h-full w-full" />}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/55 to-black/40" />
    <div className="relative mx-auto w-full max-w-[1600px] px-5 md:px-10">
      {kicker && <div className="kicker mb-5 flex items-center gap-3"><span className="h-px w-10 bg-[#5d9a35]" />{kicker}</div>}
      <MaskLines lines={[title]} className="font-display text-5xl font-extrabold leading-[0.92] text-white sm:text-7xl lg:text-8xl" />
      {lead && <Reveal delay={0.3}><p className="mt-6 max-w-xl text-white/75 md:text-lg">{lead}</p></Reveal>}
    </div>
  </section>
);

export default function Gallery() {
  const { category } = useParams();
  const { t, L, lang, settings } = useLang();
  const valid = CATEGORIES.includes(category);
  const { data } = useFetch(valid ? `/photos?category=${category}` : null);
  const list = Array.isArray(data) ? data : [];
  const copy = valid ? seoCopy(lang, category) : null;
  usePageSeo(valid ? category : "home", copy && {
    "@graph": [
      { "@type": "CollectionPage", "@id": `${SITE}/gallery/${category}#page`, url: `${SITE}/gallery/${category}`, name: copy[0], description: copy[1], inLanguage: lang,
        isPartOf: { "@id": `${SITE}/#website` }, author: { "@id": `${SITE}/#photographer` },
        mainEntity: { "@type": "ImageGallery", name: copy[0], associatedMedia: list.slice(0, 30).map((p) => imageLd(p, L)).filter(Boolean) } },
      crumbsLd([["MROUE CANVAS", "/"], [t(category), `/gallery/${category}`]]),
    ],
  });
  if (!valid) return <Navigate to="/" replace />;
  const photos = list;
  const cover = photos.find((p) => p.featured) || photos[0];
  const idx = CATEGORIES.indexOf(category);
  const next = CATEGORIES[(idx + 1) % CATEGORIES.length];
  return (
    <div data-testid={`gallery-page-${category}`}>
      <PageHero testid="gallery-hero" kicker={`${String(idx + 1).padStart(2, "0")} — ${t("gallery")} · ${photos.length} ${t("photos")}`} title={t(category)} lead={t("galleryLead")[category]} photo={cover} />
      <section className="mx-auto max-w-[1600px] px-5 py-16 md:px-10 md:py-24">
        <div className="no-scrollbar mb-10 flex gap-2 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <Link key={c} to={`/gallery/${c}`} data-testid={`gallery-tab-${c}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-xs transition-colors ${c === category ? "border-white bg-white text-black" : "border-white/20 text-white/75 hover:border-white/60"}`}>{t(c)}</Link>
          ))}
        </div>
        {data && !photos.length && <p className="text-white/60" data-testid="gallery-empty">{t("noPhotos")}</p>}
        <GalleryGrid photos={photos} layout={(settings.gallery_layouts || {})[category]} />
      </section>
      <Link to={`/gallery/${next}`} data-testid="gallery-next-link" className="group block border-t border-white/10">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-16 md:px-10">
          <span className="kicker">Next</span>
          <span className="font-display flex items-center gap-4 text-4xl font-bold text-white transition-transform duration-700 group-hover:-translate-x-3 md:text-7xl">{t(next)} <ArrowRight className="h-8 w-8 md:h-14 md:w-14" /></span>
        </div>
      </Link>
    </div>
  );
}
